import { FunctionComponent, useEffect, useMemo } from 'react';
import { sortFundNames } from '../../../../../../utilities/customSorting';
import { useFormContext } from 'react-hook-form';
import type { CreateKidDocumentValues } from '../CreateKidDocumentModal';
import { useFunds } from '../../../../../../api/funds';
import { useAllShareClasses } from '../../../../../../api/shareclass';
import TokenService from '../../../../../../services/token-service';
import { useSelector } from 'react-redux';
import {
  createSectionByIdSelector,
  createPageDetailsSelector,
} from '../../../../../../redux/pages/selectors';
import { raptorPlatformIdSelector } from '../../../../../../redux/raptorPlatform/selectors';
import { createResourceCacheKey } from '../../../../../layout/general/SimpleGeneralPageWrapper';
import {
  useGetAllKidLayoutStyles,
  useGetKidLayoutTemplate,
  useGetKidOverviewData,
} from '../../../../../../api/kids/hooks/queries';
import DuplicateDocumentSelector from './DuplicateDocumentSelector';
import NewDocumentFlow from './NewDocumentFlow';
import DocumentPreview from './ReviewDocumentPreview';

const StepDocumentType: FunctionComponent<{
  shareClassId: string;
  shareClassName: string;
}> = ({ shareClassId, shareClassName }) => {
  const { watch, setValue } = useFormContext<CreateKidDocumentValues>();
  const { user_client_id: clientId } = TokenService.getUser()!;

  const documentSourceType = watch('documentSourceType');
  const creationMethod = watch('creationMethod');
  const layoutStyle = watch('layoutStyle');
  const fundId = watch('fundId');


  // Fetch funds and share classes for duplicate selection
  const sectionDetails = useSelector(createSectionByIdSelector('raidr'));
  const pageDetails = useSelector(
    createPageDetailsSelector('kid-overview-v2', 'raidr'),
  );
  const raptorPlatformId = useSelector(raptorPlatformIdSelector);
  const rsResources = createResourceCacheKey(
    sectionDetails?.rsResources,
    pageDetails?.rsResources,
  );

  const { data: allFunds } = useFunds({
    fundTypes: sectionDetails?.fundTypes,
    rsResources: rsResources || [],
    raptorPlatformId: raptorPlatformId || undefined,
  });

  // Create a Set of all unique fund IDs from allFunds
  const uniqueFundIdsFromFunds = useMemo(() => {
    if (!allFunds) return new Set<string>();
    return new Set(allFunds.map((fund: any) => fund.fund_id));
  }, [allFunds]);

  const { data: allShareClasses } = useAllShareClasses({
    client_id: clientId,
    only_active_share_class: true,
  });

  const { data: kidOverviewData } = useGetKidOverviewData(clientId);

  // Share classes and funds that have at least one existing KID (for duplicate flow)
  const shareClassIdsWithKids = useMemo(() => {
    if (!kidOverviewData?.length) return new Set<string>();
    return new Set(kidOverviewData.map((item) => item.share_class_id));
  }, [kidOverviewData]);

  const fundIdsWithKids = useMemo(() => {
    if (!kidOverviewData?.length) return new Set<string>();
    return new Set(kidOverviewData.map((item) => item.fund_id));
  }, [kidOverviewData]);

  // Create a map of fund_id -> fund for faster lookup
  const fundsMap = useMemo(() => {
    const map = new Map<string, any>();
    allFunds?.forEach((fund: any) => {
      map.set(fund.fund_id, fund);
    });
    return map;
  }, [allFunds]);

  // For duplicate: only share classes that have existing KIDs
  const shareClassesWithKids = useMemo(() => {
    if (!allShareClasses) return undefined;
    return allShareClasses.filter((sc: any) =>
      shareClassIdsWithKids.has(sc.share_class_id),
    );
  }, [allShareClasses, shareClassIdsWithKids]);

  // For duplicate: share classes by fund, only funds/share classes with existing KIDs
  const shareClassesByFundWithKids = useMemo(() => {
    const map = new Map<string, any[]>();
    shareClassesWithKids?.forEach((sc: any) => {
      if (!fundIdsWithKids.has(sc.fund_id)) return;
      if (!map.has(sc.fund_id)) map.set(sc.fund_id, []);
      map.get(sc.fund_id)!.push(sc);
    });
    map.forEach((shareClasses) => {
      shareClasses.sort((a, b) =>
        sortFundNames(a.share_class_name_full, b.share_class_name_full),
      );
    });
    return map;
  }, [shareClassesWithKids, fundIdsWithKids]);

  // For duplicate: fund IDs that are in section funds AND have existing KIDs
  const uniqueFundIdsWithKids = useMemo(() => {
    const sectionFundIds = uniqueFundIdsFromFunds;
    const result = new Set<string>();
    fundIdsWithKids.forEach((fundId) => {
      if (sectionFundIds.has(fundId)) result.add(fundId);
    });
    return result;
  }, [uniqueFundIdsFromFunds, fundIdsWithKids]);

  // Preserve the same fund order as the overview page (KID overview data order),
  // limited to funds available in this section.
  const orderedFundIdsWithKids = useMemo(() => {
    if (!kidOverviewData?.length) return [];
    const seen = new Set<string>();
    const ordered: string[] = [];

    kidOverviewData.forEach((item) => {
      const fundId = item.fund_id;
      if (seen.has(fundId)) return;
      if (!uniqueFundIdsFromFunds.has(fundId)) return;
      seen.add(fundId);
      ordered.push(fundId);
    });

    return ordered.sort((a, b) =>
      sortFundNames(fundsMap.get(a)?.fund_name ?? a, fundsMap.get(b)?.fund_name ?? b),
    );
  }, [kidOverviewData, uniqueFundIdsFromFunds, fundsMap]);

  const { data: kidLayoutStyles, isLoading: isLoadingLayoutStyles } =
    useGetAllKidLayoutStyles(clientId);
  const flatKidLayoutStyles = useMemo(
    () => kidLayoutStyles ? Object.values(kidLayoutStyles).flat() : [],
    [kidLayoutStyles],
  );

  const selectedLayoutMetadata = useMemo(
    () =>
      flatKidLayoutStyles.find(
        (layoutMetadata) => layoutMetadata.kid_layout_id === layoutStyle,
      ),
    [flatKidLayoutStyles, layoutStyle],
  );
  const { data: selectedLayoutElements } = useGetKidLayoutTemplate(
    selectedLayoutMetadata
      ? {
        kidLayoutId: selectedLayoutMetadata.kid_layout_id,
        kidLayoutLastUpdatingTimestamp:
          selectedLayoutMetadata.last_updating_timestamp,
        fundId,
        shareClassId,
      }
      : undefined,
    {
      enabled:
        documentSourceType === 'new' &&
        creationMethod === 'recommended' &&
        !!selectedLayoutMetadata,
    },
  );

  useEffect(() => {
    if (!selectedLayoutElements) {
      return;
    }

    setValue('layoutElements', selectedLayoutElements, {
      shouldDirty: false,
      shouldValidate: false,
    });
  }, [selectedLayoutElements, setValue]);

  // Note: PDF preview generation is now handled entirely inside ReviewDocumentPreview.

  const handlePrimaryChange = (choice: 'new' | 'duplicate') => {
    setValue('documentSourceType', choice, {
      shouldDirty: true,
      shouldValidate: true,
    });
    if (choice === 'duplicate') {
      setValue('creationMethod', 'duplicate', {
        shouldDirty: true,
        shouldValidate: true,
      });
    } else {
      // Require explicit method selection for new documents.
      setValue('creationMethod', undefined, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('layoutStyle', undefined, {
        shouldDirty: false,
        shouldValidate: true,
      });
      setValue('layoutStyleName', undefined, {
        shouldDirty: false,
      });
      setValue('layoutLastUpdatedTimestamp', undefined, {
        shouldDirty: false,
      });
    }
  };

  const handleNewSubChoice = (value: 'blank' | 'recommended') => {
    setValue('creationMethod', value, {
      shouldDirty: true,
      shouldValidate: true,
    });
    // Require explicit style selection when recommended is chosen.
    if (value === 'recommended') {
      setValue('layoutStyle', undefined, {
        shouldDirty: false,
        shouldValidate: true,
      });
      setValue('layoutStyleName', undefined, {
        shouldDirty: false,
      });
      setValue('layoutLastUpdatedTimestamp', undefined, {
        shouldDirty: false,
      });
    }
  };

  const handleLayoutStyleSelect = (id: string, name: string) => {
    setValue('layoutStyle', id, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue('layoutStyleName', name, {
      shouldDirty: false,
    });
    setValue(
      'layoutLastUpdatedTimestamp',
      selectedLayoutMetadata?.kid_layout_id === id
        ? selectedLayoutMetadata.last_updating_timestamp
        : flatKidLayoutStyles.find((layout) => layout.kid_layout_id === id)
          ?.last_updating_timestamp,
      {
        shouldDirty: false,
        shouldValidate: false,
      },
    );
    setValue('layoutElements', [], {
      shouldDirty: false,
      shouldValidate: false,
    });
  };

  return (
    <fieldset className="flex h-full flex-col">
      <legend className="mb-2 text-sm font-semibold text-gray-700">
        Document Type
      </legend>

      <div className="grid h-full grid-cols-6 gap-4 overflow-hidden">
        {/* Left column: controls */}
        <div className="col-span-2 flex min-h-0 flex-col space-y-4 overflow-y-auto">
          <div className="space-y-2">
            <PrimaryChoiceCard
              isSelected={documentSourceType === 'new'}
              title="Make New Document"
              subtitle="Start a new KID document from scratch or with a recommended layout"
              onSelect={() => handlePrimaryChange('new')}
            />
            <PrimaryChoiceCard
              isSelected={documentSourceType === 'duplicate'}
              title="Duplicate Existing Document"
              subtitle="Copy content and structure from an existing KID document"
              onSelect={() => handlePrimaryChange('duplicate')}
            />
          </div>

          <legend className="mb-2 text-sm font-semibold text-gray-700">
            Document Layout
          </legend>
          {!documentSourceType && (
            <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-4 text-xs text-gray-500">
              Select a document type above to choose your document layout
              options.
            </div>
          )}
          {/* NEW DOCUMENT FLOW */}
          {documentSourceType === 'new' && (
            <NewDocumentFlow
              creationMethod={creationMethod}
              layoutStyle={layoutStyle}
              isLoadingLayoutStyles={isLoadingLayoutStyles}
              kidLayoutStyles={flatKidLayoutStyles}
              onChangeCreationMethod={handleNewSubChoice}
              onSelectTemplateStyle={handleLayoutStyleSelect}
            />
          )}

          {/* DUPLICATE DOCUMENT FLOW */}
          {documentSourceType === 'duplicate' && (
            <DuplicateDocumentSelector
              fundsMap={fundsMap}
              shareClassesByFund={shareClassesByFundWithKids}
              uniqueFundIdsFromFunds={uniqueFundIdsWithKids}
              orderedFundIds={orderedFundIdsWithKids}
              allShareClasses={shareClassesWithKids}
            />
          )}
        </div>

        {/* Right column: preview */}
        <div className="col-span-4 flex min-h-0 flex-1 flex-col">
          <DocumentPreview
            documentSourceType={documentSourceType}
            creationMethod={creationMethod}
            shareClassId={shareClassId}
            shareClassName={shareClassName}
          />
        </div>
      </div>
    </fieldset>
  );
};

const PrimaryChoiceCard: FunctionComponent<{
  isSelected: boolean;
  title: string;
  subtitle: string;
  onSelect: () => void;
}> = ({ isSelected, title, subtitle, onSelect }) => {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={
        'flex w-full cursor-pointer items-center justify-between rounded-md border p-3 text-left ' +
        (isSelected
          ? 'border-mainblue bg-blue-50'
          : 'border-gray-200 hover:bg-gray-50')
      }
    >
      <div className="flex items-start gap-3">
        <div
          className={
            'mt-0.5 h-3 w-3 rounded-full border ' +
            (isSelected ? 'border-mainblue bg-mainblue' : 'border-gray-300')
          }
        />
        <div>
          <div className="text-sm font-semibold text-gray-800">{title}</div>
          <div className="text-xs text-gray-600">{subtitle}</div>
        </div>
      </div>
    </button>
  );
};

export default StepDocumentType;
